import type React from 'react';
import { Bug, CaretRight } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { Flex, Text, TruncatedText } from '@chatsdet/ui';
import type { ExploratoryTestCandidateVerification } from '../../../features/exploratory-tests/types';
import { useIssueDetailV2 } from '../../../hooks/useIssuesV2';

type IssuesPanelProps = {
  filedVerifications: ExploratoryTestCandidateVerification[];
  pendingVerifications: ExploratoryTestCandidateVerification[];
  onOpenIssue: (issueId: string) => void;
};

export function IssuesPanel({
  filedVerifications,
  pendingVerifications,
  onOpenIssue,
}: IssuesPanelProps) {
  if (filedVerifications.length === 0 && pendingVerifications.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground px-4 text-center">
        <Text size="sm">No issues flagged yet.</Text>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto py-3 space-y-4">
      {filedVerifications.length > 0 && (
        <IssueGroup
          label="Filed"
          icon={<Bug size={14} weight="duotone" className="text-destructive" />}
          count={filedVerifications.length}
        >
          {filedVerifications.map((v) => (
            <FiledIssueRow
              key={v.id}
              issueId={v.filedIssueId!}
              onOpen={() => onOpenIssue(v.filedIssueId!)}
            />
          ))}
        </IssueGroup>
      )}

      {pendingVerifications.length > 0 && (
        <IssueGroup
          label="Candidates"
          icon={<Bug size={14} weight="duotone" className="text-amber-600 dark:text-amber-400" />}
          count={pendingVerifications.length}
        >
          {pendingVerifications.map((v) => (
            <CandidateIssueRow key={v.id} verification={v} />
          ))}
        </IssueGroup>
      )}
    </div>
  );
}

function IssueGroup({
  label,
  icon,
  count,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Flex align="center" gap="sm" className="mb-1.5 px-3">
        {icon}
        <Text weight="medium" size="xs" className="uppercase tracking-wide text-muted-foreground">
          {label}
        </Text>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          {count}
        </span>
      </Flex>
      <div className="divide-y divide-border/60 border-y border-border/60">{children}</div>
    </div>
  );
}

const severityDotClass: Record<string, string> = {
  high: 'bg-destructive',
  medium: 'bg-amber-500',
  low: 'bg-muted-foreground/50',
};

function FiledIssueRow({ issueId, onOpen }: { issueId: string; onOpen: () => void }) {
  const { data } = useIssueDetailV2(issueId, { page: 1, pageSize: 1 });
  const issue = data?.issue;

  const severity = (issue?.severity ?? 'medium').toLowerCase();
  const dotClass = severityDotClass[severity] ?? severityDotClass.medium;
  const title = issue?.title ?? 'Loading…';
  const category = issue?.category?.label ?? null;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left px-3 py-2 hover:bg-muted/40 transition-colors group"
    >
      <Flex align="start" gap="sm">
        <span
          className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <TruncatedText text={title} lines={2} className="text-xs text-foreground font-medium" />
          {category && (
            <Text size="xs" className="text-muted-foreground mt-0.5">
              {category}
            </Text>
          )}
        </div>
        <CaretRight
          size={12}
          className="text-muted-foreground/40 group-hover:text-muted-foreground shrink-0 mt-1"
        />
      </Flex>
    </button>
  );
}

function CandidateIssueRow({
  verification,
}: {
  verification: ExploratoryTestCandidateVerification;
}) {
  const severity = (verification.candidateSeverity ?? 'medium').toLowerCase();
  const dotClass = severityDotClass[severity] ?? severityDotClass.medium;
  const title = verification.verifiedTitle || verification.candidateDescription;
  const status = verification.verificationStatus;

  return (
    <div className="px-3 py-2">
      <Flex align="start" gap="sm">
        <span
          className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <TruncatedText text={title} lines={2} className="text-xs text-foreground font-medium" />
          {verification.expectedRule ? (
            <>
              <Text size="xs" className="text-amber-600 dark:text-amber-400 mt-0.5">
                Expected — matches a rule
              </Text>
              <TruncatedText
                text={verification.expectedRule.statement}
                lines={2}
                className="text-xs text-muted-foreground mt-0.5"
              />
              <Link
                to="/lookup/$id"
                params={{ id: verification.expectedRule.capabilityId }}
                className="text-xs text-primary hover:underline"
              >
                {verification.expectedRule.capabilityCode
                  ? `${verification.expectedRule.capabilityCode} · ${verification.expectedRule.capabilityTitle}`
                  : verification.expectedRule.capabilityTitle}
              </Link>
              {verification.rejectionReason && (
                <Text size="xs" className="text-muted-foreground mt-0.5">
                  {verification.rejectionReason}
                </Text>
              )}
            </>
          ) : (
            status && (
              <Text size="xs" className="text-muted-foreground mt-0.5 capitalize">
                {status.replace(/_/g, ' ')}
                {verification.rejectionReason ? ` · ${verification.rejectionReason}` : ''}
              </Text>
            )
          )}
        </div>
      </Flex>
    </div>
  );
}
